import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import router from './router'
import i18n, { applyInitialLocale } from './i18n'
import { isAbortError } from './api'

import './styles/index.css'

const app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(i18n)

/* Stamp <html lang> before first paint so assistive tech and CSS :lang()
 * see the right language from the very first frame. */
applyInitialLocale()

/* ──────────────────────────────────────────────────────────────
 * Global error handler.
 *
 * Catches anything thrown by component lifecycle hooks, render
 * functions, or watchers that wasn't already handled by an
 * `<ErrorBoundary>`. We forward to:
 *   1. The browser console (always — preserves the stack trace
 *      Vue would normally print).
 *   2. The future observability sink (Sentry / Datadog) — wired
 *      via VITE_SENTRY_DSN when that env-var is set.
 *
 * Aborted requests are not errors — the user navigated away mid-
 * fetch. Filter them out so the console + Sentry stay clean.
 * ────────────────────────────────────────────────────────────── */
app.config.errorHandler = (err, _instance, info) => {
  if (isAbortError(err)) return // not a failure

  /* `info` is a Vue-supplied string like
   * "render function" / "setup function" / "v-on handler". */

  console.error('[Lexentiqa]', info, err)

  /* Future: when Sentry is wired, forward here.
   *   if (window.Sentry?.captureException) {
   *     window.Sentry.captureException(err, { extra: { info } })
   *   }
   */
}

/* `unhandledrejection` catches promise rejections that escape every
 * async boundary in the app. Stores' fetchList swallow AbortError
 * already; this is the safety net for everything else. */
window.addEventListener('unhandledrejection', (event) => {
  if (isAbortError(event.reason)) {
    event.preventDefault() // suppress the browser warning
    return
  }

  console.error('[Lexentiqa] Unhandled promise rejection', event.reason)
})

app.mount('#app')
